import React, { FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormInputText } from '../../../../../inputs/FormInputText';
import { CustomMUIButton } from '../../../../buttons/CustomMUIButton';
import { ButtonClassNames, InputTextFormat, ModalVariants, InputRegexPattern } from '../../../../../../models/Common';
import { useAppDispatch } from '../../../../../../hooks/redux';
import { ApiError } from '../../../../../../models/ApiError';
import { formatPhone } from '../../../../../../utils';
import { userInfoApi } from '../../../../../../services/apiServices/user/userInfo';
import { modalSlice } from '../../../../../../store/reducers/ModalSlice';

interface AddNewSmsPhoneProps {
  modalTitle: string;
  description?: string;
}

const defaultProps = {
  description: '',
};

export const AddNewSmsPhone: FC<AddNewSmsPhoneProps> = ({ modalTitle, description }) => {
  const { t } = useTranslation();
  const methods = useForm<{ phoneNumber: string }>();
  const { handleSubmit, setError } = methods;
  const dispatch = useAppDispatch();
  const { openModal } = modalSlice.actions;
  const [requestConfirmationSmsCodeApi, { isLoading }] = userInfoApi.useRequestConfirmationSmsCodeMutation();

  const handleRequestConfirmationSmsCode = async ({ phoneNumber }: { phoneNumber: string }) => {
    try {
      await requestConfirmationSmsCodeApi({ phone: formatPhone(phoneNumber) })
        .unwrap()
        .then(() => {
          dispatch(
            openModal({
              modalVariants: ModalVariants.ConfirmNewSmsPhone,
              content: { phone: formatPhone(phoneNumber) },
            }),
          );
        });
    } catch (err) {
      const error = err as ApiError;
      setError('phoneNumber', {
        type: 'server',
        message: error?.data?.error?.message ?? '',
      });
    }
  };

  return (
    <div className="modals">
      <div className="modals__header">
        <article className="modals__header__title">{modalTitle}</article>
        {description && <p className="modals__header__description">{description}</p>}
      </div>
      <form className="modals__form" onSubmit={handleSubmit(handleRequestConfirmationSmsCode)}>
        <FormProvider {...methods}>
          <FormInputText
            name="phoneNumber"
            className="primary"
            label={`${t('input.phone')}`}
            isRequired
            format={InputTextFormat.Phone}
            pattern={InputRegexPattern.Phone}
            type="tel"
          />
          <div className="modals__btn-group">
            <CustomMUIButton
              label={t('buttons.add')}
              type="submit"
              className={ButtonClassNames.PrimaryFulfilled}
              isBold
              isLoading={isLoading}
            />
          </div>
        </FormProvider>
      </form>
    </div>
  );
};

AddNewSmsPhone.defaultProps = defaultProps;
